import { TrashIcon } from '@heroicons/react/24/outline';
import FormControlLabel from '@mui/material/FormControlLabel';
import { styled } from '@mui/material/styles';
import Switch, { SwitchProps } from '@mui/material/Switch';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { PaymentMethodLogo } from '../../../components/PaymentMethodLogo';
import type { PaymentConsent } from '../../../core/api/http-methods/get-payment-methods';
import { eWalletMethodView } from '../../../utils/payment-method-view';
import DeleteConfirmation from './DeleteConfirmation';
import SetPreferedConfirmation from './SetPreferredConfirmation';

interface MethodProps {
  method: PaymentConsent;
  handlePaymentMethodsModified: () => void;
}

const SwitchButton = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 50,
  height: 24,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    color: '#9A9A9A',
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(26px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.mode === 'dark' ? '#2ECA45' : '#5CB891',
        opacity: 1,
        border: 0
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5
      }
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#33cf4d',
      border: '6px solid #fff'
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color:
        theme.palette.mode === 'light'
          ? theme.palette.grey[100]
          : theme.palette.grey[600]
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: theme.palette.mode === 'light' ? 0.7 : 0.3
    }
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 20,
    height: 20
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: theme.palette.mode === 'light' ? '#E9E9EA' : '#39393D',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500
    })
  }
}));

const EWalletMethod: React.FC<MethodProps> = ({
  method,
  handlePaymentMethodsModified
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [consentId, setConsentId] = useState<string>(
    method?.payment_consent_id
  );
  const [isPreferredMethodModelOpen, setIsPreferredMethodModelOpen] =
    useState<boolean>(false);
  const handleChangePreferredMethod = () => {
    setIsPreferredMethodModelOpen(true);
  };
  const deleteClicked = (consentId: string) => {
    setConsentId(consentId);
    setIsModalOpen(true);
  };

  const view = eWalletMethodView(method);
  // Keyed by the stored method (older backend) and by the backend's logo key.
  const logoHeightMap: Record<string, string> = {
    'tng-ewallet': 'h-[33px]',
    tng: 'h-[33px]',
    'grabpay-ewallet': 'h-[47px]',
    grabpay: 'h-[47px]',
    'gcash-ewallet': 'h-[45px]',
    shopeepay: 'h-[36px]',
    'truemoney-ewallet': 'h-[36px]',
    'wechatpay-ewallet': 'h-[32px]',
    'alipay-ewallet': 'h-[32px]'
  };

  const preferred = method?.preferred;

  return (
    <div
      className="sm:w-100 my-4 min-w-[340px] max-w-[370px] rounded-3xl bg-gradient-to-r px-6 pb-1 pt-4 text-white shadow-lg sm:mx-auto md:mx-1 md:w-1/2 lg:w-1/2"
      style={{
        background: `linear-gradient(-55.329669692156095deg, rgba(248, 99, 64, 1) 0.000005960464117517737%, rgba(254, 199, 69, 1) 100.00000000000234%)`
      }}
    >
      <div className="flex items-center justify-between">
        <h6 className="text-xs">
          {preferred ? 'Your' : 'Set as'} Preferred Payment Method
        </h6>
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={() => deleteClicked(method?.payment_consent_id)}
            aria-label="Delete"
          >
            <TrashIcon className="h-6 w-6 text-white" aria-hidden="true" />
          </button>
        </div>
      </div>
      <div className="mt-1 px-3">
        <FormControlLabel
          control={
            <SwitchButton
              checked={method?.preferred}
              onChange={handleChangePreferredMethod}
            />
          }
          label=""
        />
      </div>
      <div className="flex items-center justify-between">
        <div className="">
          <span className="mt-4 block text-xs font-light">Method</span>
          <h2 className="text-xl font-bold tracking-wider">{view.name}</h2>
        </div>
        <div>
          <PaymentMethodLogo
            logo={view.logo}
            imgClassName={`inline-block ${
              logoHeightMap[view.logoSizeKey] || '45px'
            } object-contain align-middle`}
            glyphClassName="inline-block h-[40px] w-[40px] align-middle text-white"
          />
        </div>
      </div>
      <div className="mb-2 mt-12 flex items-center justify-start gap-x-10">
        <div>
          <h4 className="text-xs font-light">Status</h4>
          <h3 className="text-md font-bold tracking-wide">{method.status}</h3>
        </div>
        <div>
          <h4 className="text-xs font-light">Added</h4>
          <h3 className="text-md font-bold tracking-wide">
            {' '}
            {dayjs(method.created_at).format('DD MMM YYYY')}
          </h3>
        </div>
      </div>
      {isModalOpen ? (
        <DeleteConfirmation
          open={isModalOpen}
          setOpen={setIsModalOpen}
          consentId={consentId}
          handlePaymentMethodsModified={handlePaymentMethodsModified}
        />
      ) : (
        <></>
      )}
      {isPreferredMethodModelOpen ? (
        <SetPreferedConfirmation
          isAlreadyPreferred={method?.preferred}
          open={isPreferredMethodModelOpen}
          setOpen={setIsPreferredMethodModelOpen}
          consentId={method?.payment_consent_id}
          method={view.dialogName}
          handlePaymentMethodsModified={handlePaymentMethodsModified}
        />
      ) : (
        <></>
      )}
    </div>
  );
};

export default EWalletMethod;
